/**
 * Nova skill display metadata — shared between server skill registry and
 * client UI (ActiveSkillChip, the starter prompt a session sends).
 *
 * Aliases: skill metadata, skill labels, skill display, asset skills
 */

export type SkillName =
  | "voice-onboarding"
  | "coach-story"
  | "profile-update"
  | "linkedin-profile"
  | "cih-article"
  | "linkedin-post"
  | "outreach-scripts"
  | "offer-hooks"
  | "mini-sprint"
  | "mini-sprint-video-script"
  | "authority-video"
  | "transformation-story"
  | "success-story"
  | "mindset-check"
  | "wheel-page-copy";

export function isSkillName(value: string): value is SkillName {
  return (
    value === "voice-onboarding" ||
    value === "coach-story" ||
    value === "profile-update" ||
    value === "linkedin-profile" ||
    value === "cih-article" ||
    value === "linkedin-post" ||
    value === "outreach-scripts" ||
    value === "offer-hooks" ||
    value === "mini-sprint" ||
    value === "mini-sprint-video-script" ||
    value === "authority-video" ||
    value === "transformation-story" ||
    value === "success-story" ||
    value === "mindset-check" ||
    value === "wheel-page-copy"
  );
}

export interface SkillDisplayMetadata {
  /** Human-readable name surfaced in the ActiveSkillChip and gating copy. */
  label: string;
  /** One-line teaser shown under the label on the welcome screen. */
  sublabel: string;
  /** Natural-language prompt sent when the welcome button is clicked. */
  starterPrompt: string;
}

export const SKILL_DISPLAY_METADATA: Record<SkillName, SkillDisplayMetadata> = {
  "voice-onboarding": {
    label: "Your voice",
    sublabel: "How you sound on social, in email, and in a DM",
    starterPrompt: "Set up my voice profile and channel guides.",
  },
  "coach-story": {
    label: "Your story",
    sublabel: "Five reusable cuts of your origin story and your why",
    starterPrompt: "Help me write my story in the cuts I can reuse.",
  },
  "profile-update": {
    label: "Profile update",
    sublabel: "Refresh your public coach profile",
    starterPrompt: "Help me update my public coach profile.",
  },
  "linkedin-profile": {
    label: "LinkedIn profile",
    sublabel: "Headline, About, Featured, Services",
    starterPrompt: "Help me rewrite my LinkedIn profile.",
  },
  "cih-article": {
    label: "Article",
    sublabel: "Draft a coaching article on a published challenge",
    starterPrompt: "Help me draft a coaching article.",
  },
  "linkedin-post": {
    label: "LinkedIn post",
    sublabel: "Write a LinkedIn post",
    starterPrompt: "Help me write a LinkedIn post.",
  },
  "outreach-scripts": {
    label: "Outreach scripts",
    sublabel: "Draft your LinkedIn outreach pack",
    starterPrompt: "Help me draft my outreach scripts.",
  },
  "wheel-page-copy": {
    label: "Your Wheel of Life page",
    sublabel: "Your intro, next steps and contact line, in your voice",
    starterPrompt: "Help me write my Wheel of Life page text.",
  },
  "offer-hooks": {
    label: "Offer hooks",
    sublabel: "A bank of opening lines that sell your core offer",
    starterPrompt: "Build a bank of hooks for my offer.",
  },
  "mini-sprint": {
    label: "Mini Sprint",
    sublabel: "Design your 5-day entry offer",
    starterPrompt: "Help me design my Mini Sprint.",
  },
  "mini-sprint-video-script": {
    label: "Mini Sprint video script",
    sublabel: "Script the video for your Mini Sprint offer page",
    starterPrompt:
      "Help me write the video script for my Mini Sprint offer page.",
  },
  "authority-video": {
    label: "Authority Video",
    sublabel: "90-sec authority script + shorts + call map",
    starterPrompt: "Help me write my authority video scripts.",
  },
  "transformation-story": {
    label: "Profile testimonial",
    sublabel: "An SSS-format client testimonial for your public coach profile",
    starterPrompt: "Help me draft a client testimonial for my coach profile.",
  },
  "success-story": {
    label: "CIH success story",
    sublabel:
      "Submit a client outcome for DCM review and possible CIH publication",
    starterPrompt: "Help me submit a client success story for the CIH.",
  },
  // Not on the welcome screen: it runs before the first outward skill, and
  // from Plan's trial-day offer.
  "mindset-check": {
    label: "Mindset check",
    sublabel: "Perfectionist Coach or Builder Coach, and one first step today",
    starterPrompt: "Let's do my Mindset check.",
  },
};

/**
 * Skills that stay registered and loadable, so old conversations and saved
 * deliverables keep working, but that no asset card opens.
 */
export const HIDDEN_SKILLS: readonly SkillName[] = ["authority-video"];

/**
 * Skills that start on their own, never from a menu: the Mindset check runs
 * before the first outward skill and from Plan's offer. Unlike a hidden skill
 * it starts fresh runs; the `prerequisiteSkill` of a gated skill is what
 * names it to the model.
 */
export const PREREQUISITE_ONLY_SKILLS: readonly SkillName[] = ["mindset-check"];

/**
 * The once-per-coach setup skills: they run before another asset, in its
 * conversation, and their finished run is never that conversation's one asset
 * while the asset they were run for is still to come. A test holds this list
 * to the registry's `once-per-coach` skills.
 */
export const SETUP_SKILLS: readonly SkillName[] = [
  "voice-onboarding",
  "mindset-check",
];
